import React from "react";
import { filter, indexOf, map } from "lodash";
import styled from "styled-components";
import { Spacing, content } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import Chip from "@ui-kit/form/chip";
import { getColor } from "@ui-kit/form/search/token";
import HoverCard from "@ui-kit/hover-card";
import { tr } from "@radarly/i18n";

type OperatorType = "operator" | "phrase" | "field" | "base";

export interface IProps {
  locale: Locale;
  onAddOperator: (operatorId: string, operatorSuffix: string) => void;
  className?: string;
  restrictedOperators?: boolean;
  isDisabled?: boolean;
}

interface Operator {
  id: string;
  i18nKey: string;
  suffix?: string;
  help: Array<{
    type: OperatorType;
    label: string;
  }>;
}

const operatorType = (type: OperatorType) => (label: string) => ({
  type,
  label,
});

const operator = operatorType("operator");
const phrase = operatorType("phrase");
const field = operatorType("field");

const OPERATORS: Operator[] = [
  {
    id: "AND",
    i18nKey: "AND",
    suffix: " ",
    help: [phrase('"A"'), operator(" AND "), phrase('"B"')],
  },
  {
    id: "OR",
    i18nKey: "OR",
    suffix: " ",
    help: [phrase('"A"'), operator(" OR "), phrase('"B"')],
  },
  {
    id: "NOT",
    i18nKey: "NOT",
    suffix: " ",
    help: [phrase('"A"'), operator(" NOT "), phrase('"B"')],
  },
  {
    id: "NEAR",
    i18nKey: "NEAR",
    suffix: "/",
    help: [phrase('"A"'), operator(" NEAR/5 "), phrase('"B"')],
  },
  {
    id: "raw:",
    i18nKey: "RAW",
    help: [field("raw:"), phrase('"h&m"')],
  },
  {
    id: "@",
    i18nKey: "AT",
    help: [phrase("@linkfluence")],
  },
  {
    id: "#",
    i18nKey: "HASHTAG",
    help: [phrase("#linkfluence")],
  },
  {
    id: "author:@",
    i18nKey: "AUTHOR_TWITTER_INSTAGRAM",
    help: [field("author:"), phrase("@screenname")],
  },
  {
    id: "author:",
    i18nKey: "AUTHOR_FACEBOOK",
    help: [field("author:"), phrase("name")],
  },
  {
    id: "site:",
    i18nKey: "SITE",
    help: [field("site:"), phrase("hm.com")],
  },
];

const restrictedOperatorsList = filter(
  OPERATORS,
  (operator) => indexOf(["OR", "AND", "NOT"], operator.id) !== -1
);

const Title = styled.div`
  color: ${content};
  margin-bottom: ${Spacing.ExtraSmall};
`;

const Text = styled.div`
  color: ${(props) => content(props, "secondary")};
  margin-bottom: ${Spacing.Medium};
`;

const Operators = styled.div<{ isDisabled?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.Medium};
  cursor: ${({ isDisabled }) => (isDisabled ? "default" : "inherit")};
  > a {
    cursor: default;
  }
`;

const Label = styled.div`
  font-family: monospace;
`;

const Help = styled.span<{
  type: OperatorType;
}>`
  color: ${(props) => getColor(props.type)};
`;

const Description = styled.div`
  color: ${(props) => content(props, "secondary")};
`;

const FilterbarPanelsSearch = ({
  locale,
  onAddOperator,
  className,
  restrictedOperators = false,
  isDisabled = false,
}: IProps) => {
  const onClickOperator = (
    e: React.MouseEvent<HTMLDivElement>,
    operator: string,
    operatorSuffix: string = ""
  ) => {
    e.stopPropagation();
    onAddOperator(operator, operatorSuffix);
  };

  return (
    <div className={className}>
      <Title>{tr(locale, `SEARCH::OPERATOR::HELP`)}</Title>
      <Text>{tr(locale, `SEARCH::OPERATOR::TIP`)}</Text>
      <Operators isDisabled={isDisabled}>
        {map(
          restrictedOperators ? restrictedOperatorsList : OPERATORS,
          ({ id, i18nKey, suffix, help }) => (
            <HoverCard
              key={id}
              trigger={
                <Chip
                  label={id}
                  size="small"
                  status="selected"
                  onClick={
                    isDisabled
                      ? null
                      : (event) => onClickOperator(event, id, suffix)
                  }
                />
              }
              content={
                <>
                  <Label>
                    {map(help, ({ label, type }) => (
                      <Help key={label} type={type}>
                        {label}
                      </Help>
                    ))}
                  </Label>
                  <Description>
                    {tr(locale, `SEARCH::HELP_SHORTCUTS::${i18nKey}`)}
                  </Description>
                </>
              }
            />
          )
        )}
      </Operators>
    </div>
  );
};

export default FilterbarPanelsSearch;
